import axiosInstance from "./service-instance";

// Chitthi is the telephony backend that fronts the Ozonetel CloudAgent vendor.
// We call it through the LOS backend's own proxy (/case/chitthi/...) rather
// than chitthi.progcap.com directly — the proxy attaches the service-to-service
// CHITTHI_SERVICE_TOKEN and the authenticated user server-side.

/**
 * Initiates an outbound Ozonetel call to `customerNumber` on behalf of the
 * logged-in LOS user. Chitthi connects the agent leg first, then the customer.
 *
 * Note: los_user_id/token/source are NOT sent — the LOS proxy overwrites them
 * server-side from the authenticated user and CHITTHI_SERVICE_TOKEN regardless
 * of what's sent.
 *
 * @param {string|number} customerNumber 10-digit customer mobile number
 * @param {string|number} caseId          current case id the call belongs to
 */
export const initiateOzonetelManualCall = (customerNumber, caseId) =>
    axiosInstance.post("/case/chitthi/call/manual/", {
        vendor: "OZONETEL",
        customer_number: customerNumber,
        case_id: caseId
    });

/**
 * Fetches telephony Call Detail Records (CDR) for the CREDIT team, scoped to a
 * case, from Chitthi. Server-paginated.
 *
 * @param {object}        opts
 * @param {string|number} opts.caseId    current case id
 * @param {number}        opts.page       1-based page number
 * @param {number}        opts.pageSize   rows per page
 */
export const fetchCallLogs = ({
    caseId,
    page = 1,
    pageSize = 10,
    status
} = {}) =>
    axiosInstance.get("/case/chitthi/cdr/", {
        params: {
            team: "CREDIT",
            page,
            page_size: pageSize,
            case_id: caseId,
            // Omit when "All" is selected (empty) so results aren't filtered.
            ...(status ? { status } : {})
        }
    });

/**
 * Schedules a Microsoft Teams call for a case. Chitthi creates a real Outlook
 * calendar event on the organizer's calendar and sends the invitation to the
 * recipient over the chosen channel (WhatsApp / email).
 *
 * @param {object} payload
 * @param {string} payload.subject          meeting subject (required)
 * (organizer_email is NOT sent — the LOS proxy sets it to a static mailbox)
 * @param {string} payload.start_time        ISO8601 w/ offset, e.g. 2026-07-27T11:41:03+05:30
 * @param {string} payload.end_time          ISO8601 w/ offset, must be > start_time
 * @param {string} payload.user_email        login user email
 * @param {string|number} payload.user_id    login user id
 * @param {string} [payload.invite_email]    customer email
 * @param {string} [payload.invite_mobile]   customer mobile
 * @param {string[]} [payload.attendee_emails] calendar attendees
 * @param {string} payload.source            always "LOS"
 * @param {string|number} [payload.case_id]
 * @param {string|number} [payload.company_id]
 */
export const scheduleTeamsCall = payload =>
    axiosInstance.post("/case/teams-call/schedule/", payload);

/**
 * Lists scheduled/past Teams calls (server-paginated, page_size 50). Filter by
 * `caseId` (a case's calls) and/or `userId` (the calls a given LOS user booked —
 * powers the personal "My Calls" calendar).
 * @param {object} opts
 * @param {string|number} [opts.caseId]
 * @param {string|number} [opts.userId]
 * @param {number} [opts.page]
 */
export const fetchTeamsCalls = ({ caseId, userId, page = 1 } = {}) =>
    axiosInstance.get("/case/teams-call/list/", {
        params: {
            ...(caseId != null ? { case_id: caseId } : {}),
            ...(userId != null ? { user: userId } : {}),
            page
        }
    });

/**
 * Fetches a single Teams call's detail (includes `join_url` + artefacts).
 * @param {string} meetingId Graph meeting id (call.meeting_id)
 */
export const fetchTeamsCallDetail = meetingId =>
    axiosInstance.get(`/case/teams-call/${meetingId}/`);

/**
 * Cancels a scheduled Teams call (removes the calendar block, notifies attendees).
 * @param {string} meetingId Graph meeting id (call.meeting_id)
 */
export const cancelTeamsCall = meetingId =>
    // Body is empty — the LOS proxy injects the canceller identity server-side.
    axiosInstance.post(`/case/teams-call/${meetingId}/cancel/`, {});

// The API returns `status` as an integer, 1-based in enum order:
// 1 scheduled · 2 in_progress · 3 artefacts_pending · 4 completed · 5 cancelled · 6 failed.
export const CALL_STATUS_BY_CODE = {
    1: "scheduled",
    2: "in_progress",
    3: "artefacts_pending",
    4: "completed",
    5: "cancelled",
    6: "failed"
};

/**
 * Normalises a call `status` to the canonical string key the UI uses. Accepts an
 * integer code (or numeric string) and maps it via CALL_STATUS_BY_CODE; passes an
 * existing string status through unchanged (back-compat).
 */
export const normalizeCallStatus = status => {
    if (status == null) return "";
    const n = Number(status);
    if (!Number.isNaN(n) && CALL_STATUS_BY_CODE[n]) {
        return CALL_STATUS_BY_CODE[n];
    }
    return String(status);
};

// Human-readable labels keyed by the canonical status key (for display).
export const CALL_STATUS_LABELS = {
    scheduled: "Scheduled",
    in_progress: "In Progress",
    artefacts_pending: "Artefacts Pending",
    completed: "Completed",
    cancelled: "Cancelled",
    failed: "Failed"
};

// Label for a raw status (integer code or string key).
export const callStatusLabel = status => {
    const key = normalizeCallStatus(status);
    return CALL_STATUS_LABELS[key] || String(key).replace(/_/g, " ");
};

/**
 * Extracts { results, next } from a teams-call list response, tolerating the
 * shapes the LOS proxy / chitthi may return:
 *   [...] | { results, next } | { data: [...] } | { data: { results, next } }
 *   | { success, message, data: { count, results, next } }
 */
export const teamsCallsPage = data => {
    const body = data || {};
    // Unwrap a { success, message, data: {...} } envelope to the page object.
    const page =
        body.data && typeof body.data === "object" && !Array.isArray(body.data)
            ? body.data
            : body;
    const results = Array.isArray(page)
        ? page
        : Array.isArray(page.results)
        ? page.results
        : Array.isArray(page.data)
        ? page.data
        : [];
    const next = page && !Array.isArray(page) ? page.next || null : null;
    return { results, next };
};

export default {
    initiateOzonetelManualCall,
    fetchCallLogs,
    scheduleTeamsCall,
    fetchTeamsCalls,
    fetchTeamsCallDetail,
    cancelTeamsCall,
    teamsCallsPage
};

